import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Wrench, Plus, Search, Loader2, Edit3, Trash2, Power } from "lucide-react";
import { toast } from "sonner";
import { ImageUploader, PhotoThumb } from "../components/ImageUploader";

const CATEGORIAS = [
  { value: "eletrico", label: "Elétrico" },
  { value: "mecanico", label: "Mecânico" },
  { value: "hidraulico", label: "Hidráulico" },
  { value: "mao_de_obra", label: "Mão de Obra" },
  { value: "outro", label: "Outro" },
];

const UNIDADES = [
  { value: "un", label: "un" },
  { value: "hr", label: "hr" },
  { value: "m2", label: "m²" },
  { value: "kg", label: "kg" },
  { value: "outro", label: "outro" },
];

const fmtBRL = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function ItemModal({ open, onOpenChange, editing, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    tipo: "servico", codigo: "", descricao: "", unidade: "un",
    valor_unitario: "", categoria: "mecanico", status: "ativo",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        tipo: editing.tipo, codigo: editing.codigo, descricao: editing.descricao,
        unidade: editing.unidade, valor_unitario: String(editing.valor_unitario),
        categoria: editing.categoria, status: editing.status,
      } : {
        tipo: "servico", codigo: "", descricao: "", unidade: "un",
        valor_unitario: "", categoria: "mecanico", status: "ativo",
      });
    }
  }, [open, editing]);

  const salvar = async () => {
    if (!form.descricao || form.descricao.length < 3) {
      toast.error("Descrição obrigatória (mín. 3 caracteres)");
      return;
    }
    const valor = parseFloat(String(form.valor_unitario).replace(",", "."));
    if (Number.isNaN(valor) || valor < 0) {
      toast.error("Valor unitário inválido");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, valor_unitario: valor };
      if (!payload.codigo) delete payload.codigo;
      if (isEdit) {
        // PATCH não aceita tipo/codigo (imutáveis)
        const { tipo: _t, codigo: _c, ...rest } = payload;
        void _t; void _c;
        await api.patch(`/cadastro/pecas-servicos/${editing.id}`, rest);
        toast.success("Item atualizado");
      } else {
        await api.post("/cadastro/pecas-servicos", payload);
        toast.success("Item criado");
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="item-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar item" : "Novo item de Peça/Serviço"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isEdit ? "Edição limitada (tipo e código não mudam)." : "Código será gerado automaticamente se vazio."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })} disabled={isEdit}>
              <SelectTrigger data-testid="item-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="servico">Serviço</SelectItem>
                <SelectItem value="peca">Peça</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Código</Label>
            <Input
              data-testid="item-codigo-input"
              value={form.codigo}
              placeholder="(auto)"
              onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
              disabled={isEdit}
            />
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider">Descrição</Label>
          <Input
            data-testid="item-descricao-input"
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            placeholder="Ex: Reposição de acrílico de proteção EJC216"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Unidade</Label>
            <Select value={form.unidade} onValueChange={(v) => setForm({ ...form, unidade: v })}>
              <SelectTrigger data-testid="item-unidade-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNIDADES.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Categoria</Label>
            <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
              <SelectTrigger data-testid="item-categoria-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIAS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Valor unitário</Label>
            <Input
              data-testid="item-valor-input"
              type="number" step="0.01" min="0"
              value={form.valor_unitario}
              onChange={(e) => setForm({ ...form, valor_unitario: e.target.value })}
              placeholder="0,00"
            />
          </div>
        </div>

        {isEdit && editing?.id && (
          <div className="pt-3 border-t">
            <ImageUploader
              photos={editing.fotos || []}
              modulo="peca"
              refId={editing.id}
              label="Fotos do item"
              onChange={(fotos) => { editing.fotos = fotos; }}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="item-modal-cancel">Cancelar</Button>
          <Button onClick={salvar} disabled={saving} data-testid="item-modal-save"
            className="bg-orange-600 hover:bg-orange-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isEdit ? "Salvar alterações" : "Criar item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CadastroPecasServicos() {
  const { user } = useAuth();
  const canEdit = ["diretor", "gerente"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("todos");
  const [modal, setModal] = useState({ open: false, editing: null });
  const [delDialog, setDelDialog] = useState({ open: false, item: null });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (tab !== "todos") params.tipo = tab;
      const { data } = await api.get("/cadastro/pecas-servicos", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erro ao carregar itens");
    } finally {
      setLoading(false);
    }
  }, [q, tab]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = async (item) => {
    try {
      await api.patch(`/cadastro/pecas-servicos/${item.id}/toggle`);
      toast.success(`Item ${item.status === "ativo" ? "inativado" : "ativado"}`);
      carregar();
    } catch (e) {
      toast.error("Erro ao alternar status");
    }
  };

  const excluir = async () => {
    if (!delDialog.item) return;
    try {
      await api.delete(`/cadastro/pecas-servicos/${delDialog.item.id}`);
      toast.success("Item excluído");
      setDelDialog({ open: false, item: null });
      carregar();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="space-y-4" data-testid="cadastro-pecas-servicos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-orange-600" />
            Peças e Serviços
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Cadastro de itens reutilizáveis nas Propostas Pós-venda
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setModal({ open: true, editing: null })}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="btn-novo-item"
          >
            <Plus className="w-4 h-4 mr-2" /> Adicionar
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="todos" data-testid="tab-todos">Todos</TabsTrigger>
              <TabsTrigger value="servico" data-testid="tab-servicos">Serviços</TabsTrigger>
              <TabsTrigger value="peca" data-testid="tab-pecas">Peças</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input
              data-testid="input-busca"
              className="pl-8 h-9"
              placeholder="Buscar por código ou descrição"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            Nenhum item encontrado. {canEdit && "Clique em + Adicionar para criar."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">FOTO</TableHead>
                <TableHead className="w-24">Código</TableHead>
                <TableHead className="w-20">Tipo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="w-32">Categoria</TableHead>
                <TableHead className="w-20 text-center">Un.</TableHead>
                <TableHead className="w-32 text-right">Valor Unit.</TableHead>
                <TableHead className="w-24 text-center">Status</TableHead>
                {canEdit && <TableHead className="w-32 text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`row-item-${it.id}`}>
                  <TableCell><PhotoThumb photos={it.fotos} size={36} alt={it.descricao} /></TableCell>
                  <TableCell className="font-mono text-xs">{it.codigo}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] font-mono">
                      {it.tipo === "servico" ? "SERV" : "PEÇA"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{it.descricao}</TableCell>
                  <TableCell>
                    <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-[10px] uppercase">
                      {CATEGORIAS.find((c) => c.value === it.categoria)?.label || it.categoria}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-sm">{it.unidade}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtBRL(it.valor_unitario)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={it.status === "ativo"
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]"
                      : "bg-slate-200 text-slate-700 border-slate-300 text-[10px]"}>
                      {it.status === "ativo" ? "ATIVO" : "INATIVO"}
                    </Badge>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => toggle(it)}
                          data-testid={`btn-toggle-${it.id}`}
                          title={it.status === "ativo" ? "Inativar" : "Ativar"}>
                          <Power className={`w-3.5 h-3.5 ${it.status === "ativo" ? "text-emerald-600" : "text-slate-400"}`} />
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => setModal({ open: true, editing: it })}
                          data-testid={`btn-edit-${it.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setDelDialog({ open: true, item: it })}
                          data-testid={`btn-del-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <ItemModal
        open={modal.open}
        onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}
        editing={modal.editing}
        onSaved={carregar}
      />

      <Dialog open={delDialog.open} onOpenChange={(o) => setDelDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="del-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Excluir item?</DialogTitle>
            <DialogDescription>
              {delDialog.item ? `"${delDialog.item.descricao}" (${delDialog.item.codigo})` : ""} será excluído (soft delete).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDialog({ open: false, item: null })}>Cancelar</Button>
            <Button onClick={excluir} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-del">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
